import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from '../../api/api';

interface PerfectMatchState {
    code: string | null;
    count: number | null;
    loading: boolean;
    error: string | null;
    resultType: 'type1' | 'type2' | null;
    // Auth
    token: string | null;
    isAuthenticated: boolean;
    authLoading: boolean;
    authError: string | null;
}

const initialState: PerfectMatchState = {
    code: null,
    count: null,
    loading: false,
    error: null,
    resultType: null,
    // Auth
    token: localStorage.getItem('perfectMatchToken'),
    isAuthenticated: !!localStorage.getItem('perfectMatchToken'),
    authLoading: false,
    authError: null,
};

export const loginPerfectMatch = createAsyncThunk(
    'perfectMatch/login',
    async (credentials: { username: string; password: string }, { rejectWithValue }) => {
        try {
            const response = await apiInstance.post('/perfect-match/login', credentials);
            
            if (response.data.success) {
                const { token } = response.data.data;
                localStorage.setItem('perfectMatchToken', token);
                return { token };
            } else {
                return rejectWithValue(response.data.message || 'Autentificare eșuată');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Eroare la autentificare');
        }
    }
);

export const fetchNextPerfectMatchCode = createAsyncThunk(
    'perfectMatch/fetchNext',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('perfectMatchToken');
            
            const response = await apiInstance.get('/perfect-match/next-code', {
                headers: {
                    'X-Perfect-Match-Token': token
                }
            });
            
            if (response.data.success) {
                const { code, count } = response.data.data;
                return { code, count };
            } else {
                return rejectWithValue(response.data.message || 'Failed to fetch code');
            }
        } catch (error: any) {
            // Dacă token expirat, logout automat
            if (error.response?.status === 401) {
                localStorage.removeItem('perfectMatchToken');
                dispatch(logoutPerfectMatch());
            }
            return rejectWithValue(error.response?.data?.message || 'Error fetching perfect match code');
        }
    }
);

export const logoutPerfectMatch = createAsyncThunk(
    'perfectMatch/logout',
    async () => {
        localStorage.removeItem('perfectMatchToken');
        return null;
    }
);

const perfectMatchSlice = createSlice({
    name: 'perfectMatch',
    initialState,
    reducers: {
        clearPerfectMatchCode: (state) => {
            state.code = null;
            state.count = null;
        },
        setResultType: (state, action) => {
            state.resultType = action.payload;
        },
        checkAuth: (state) => {
            const token = localStorage.getItem('perfectMatchToken');
            state.isAuthenticated = !!token;
            state.token = token;
        }
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(loginPerfectMatch.pending, (state) => {
                state.authLoading = true;
                state.authError = null;
            })
            .addCase(loginPerfectMatch.fulfilled, (state, action) => {
                state.authLoading = false;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(loginPerfectMatch.rejected, (state, action) => {
                state.authLoading = false;
                state.authError = action.payload as string;
                state.isAuthenticated = false;
            })
            // Logout
            .addCase(logoutPerfectMatch.fulfilled, (state) => {
                state.isAuthenticated = false;
                state.token = null;
            })
            // Fetch Code
            .addCase(fetchNextPerfectMatchCode.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNextPerfectMatchCode.fulfilled, (state, action) => {
                state.loading = false;
                state.code = action.payload.code;
                state.count = action.payload.count;
            })
            .addCase(fetchNextPerfectMatchCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearPerfectMatchCode, setResultType, checkAuth } = perfectMatchSlice.actions;
export default perfectMatchSlice.reducer;